/**
 * Mobile touch controls: virtual joystick (left) for movement,
 * drag on the right half for camera look, and buttons for
 * jump / break (hold) / place. Writes directly into the shared
 * InputState so the rest of the game is input-source agnostic.
 */

import type { InputState } from "../core/input";

export function isTouchDevice(): boolean {
  // "pointer: coarse" targets devices whose PRIMARY input is touch —
  // touchscreen laptops with a mouse stay on the desktop experience.
  if (typeof window.matchMedia === "function") {
    return window.matchMedia("(pointer: coarse)").matches;
  }
  return "ontouchstart" in window;
}

export type TouchControls = {
  dispose: () => void;
};

const JOYSTICK_SIZE = 120;
const KNOB_SIZE = 52;
const LOOK_SENSITIVITY = 2.2; // multiplies touch px into mouse-delta px

export function createTouchControls(input: InputState): TouchControls {
  const root = document.createElement("div");
  root.id = "touch-controls";
  root.style.cssText = `
    position: fixed;
    inset: 0;
    z-index: 12;
    pointer-events: none;
    user-select: none;
    -webkit-user-select: none;
  `;

  // ─── Virtual joystick (bottom-left) ───
  const joyBase = document.createElement("div");
  joyBase.style.cssText = `
    position: absolute;
    left: 24px;
    bottom: 96px;
    width: ${JOYSTICK_SIZE}px;
    height: ${JOYSTICK_SIZE}px;
    border-radius: 50%;
    background: rgba(255,255,255,0.08);
    border: 2px solid rgba(255,255,255,0.25);
    pointer-events: auto;
    touch-action: none;
  `;

  const knob = document.createElement("div");
  knob.style.cssText = `
    position: absolute;
    left: ${(JOYSTICK_SIZE - KNOB_SIZE) / 2}px;
    top: ${(JOYSTICK_SIZE - KNOB_SIZE) / 2}px;
    width: ${KNOB_SIZE}px;
    height: ${KNOB_SIZE}px;
    border-radius: 50%;
    background: rgba(255,255,255,0.35);
    border: 2px solid rgba(255,255,255,0.5);
  `;
  joyBase.appendChild(knob);
  root.appendChild(joyBase);

  let joyTouchId: number | null = null;

  function updateJoystick(clientX: number, clientY: number) {
    const rect = joyBase.getBoundingClientRect();
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    let dx = (clientX - cx) / (rect.width / 2);
    let dy = (clientY - cy) / (rect.height / 2);
    const len = Math.sqrt(dx * dx + dy * dy);
    if (len > 1) {
      dx /= len;
      dy /= len;
    }

    knob.style.transform = `translate(${dx * 34}px, ${dy * 34}px)`;

    const DEAD = 0.25;
    input.forward = dy < -DEAD;
    input.backward = dy > DEAD;
    input.left = dx < -DEAD;
    input.right = dx > DEAD;
    input.sprint = len > 0.95 && input.forward;
  }

  function resetJoystick() {
    joyTouchId = null;
    knob.style.transform = "translate(0px, 0px)";
    input.forward = false;
    input.backward = false;
    input.left = false;
    input.right = false;
    input.sprint = false;
  }

  joyBase.addEventListener("touchstart", (e) => {
    e.preventDefault();
    if (joyTouchId !== null) return; // a second finger must not steal the stick
    const touch = e.changedTouches[0];
    joyTouchId = touch.identifier;
    updateJoystick(touch.clientX, touch.clientY);
  });
  joyBase.addEventListener("touchmove", (e) => {
    e.preventDefault();
    for (const touch of Array.from(e.changedTouches)) {
      if (touch.identifier === joyTouchId) updateJoystick(touch.clientX, touch.clientY);
    }
  });
  const joyEnd = (e: TouchEvent) => {
    for (const touch of Array.from(e.changedTouches)) {
      if (touch.identifier === joyTouchId) resetJoystick();
    }
  };
  joyBase.addEventListener("touchend", joyEnd);
  joyBase.addEventListener("touchcancel", joyEnd);

  // ─── Look area (right half of the screen) ───
  const lookArea = document.createElement("div");
  lookArea.style.cssText = `
    position: absolute;
    right: 0;
    top: 0;
    width: 55%;
    height: calc(100% - 84px); /* keep the hotbar tappable below */
    pointer-events: auto;
    touch-action: none;
  `;
  root.appendChild(lookArea);

  let lookTouchId: number | null = null;
  let lastLookX = 0;
  let lastLookY = 0;

  lookArea.addEventListener("touchstart", (e) => {
    if (lookTouchId !== null) return;
    const touch = e.changedTouches[0];
    lookTouchId = touch.identifier;
    lastLookX = touch.clientX;
    lastLookY = touch.clientY;
  });
  lookArea.addEventListener("touchmove", (e) => {
    e.preventDefault();
    for (const touch of Array.from(e.changedTouches)) {
      if (touch.identifier !== lookTouchId) continue;
      input.mouseDeltaX += (touch.clientX - lastLookX) * LOOK_SENSITIVITY;
      input.mouseDeltaY += (touch.clientY - lastLookY) * LOOK_SENSITIVITY;
      lastLookX = touch.clientX;
      lastLookY = touch.clientY;
    }
  });
  const lookEnd = (e: TouchEvent) => {
    for (const touch of Array.from(e.changedTouches)) {
      if (touch.identifier === lookTouchId) lookTouchId = null;
    }
  };
  lookArea.addEventListener("touchend", lookEnd);
  lookArea.addEventListener("touchcancel", lookEnd);

  // ─── Action buttons (bottom-right) ───
  function actionButton(label: string, bottom: number, right: number): HTMLDivElement {
    const button = document.createElement("div");
    button.textContent = label;
    button.style.cssText = `
      position: absolute;
      right: ${right}px;
      bottom: ${bottom}px;
      width: 62px;
      height: 62px;
      border-radius: 50%;
      background: rgba(255,255,255,0.12);
      border: 2px solid rgba(255,255,255,0.35);
      color: #fff;
      font-size: 22px;
      display: flex;
      align-items: center;
      justify-content: center;
      pointer-events: auto;
      touch-action: none;
      text-shadow: 0 1px 3px rgba(0,0,0,0.8);
    `;
    root.appendChild(button);
    return button;
  }

  function bindHold(button: HTMLDivElement, key: "jump" | "attack" | "interact") {
    const press = (e: TouchEvent) => {
      e.preventDefault();
      e.stopPropagation();
      input[key] = true;
      button.style.background = "rgba(255,255,255,0.35)";
    };
    const release = (e: TouchEvent) => {
      e.preventDefault();
      input[key] = false;
      button.style.background = "rgba(255,255,255,0.12)";
    };
    button.addEventListener("touchstart", press);
    button.addEventListener("touchend", release);
    button.addEventListener("touchcancel", release);
  }

  const jumpButton = actionButton("⬆", 96, 24);
  bindHold(jumpButton, "jump");
  const breakButton = actionButton("⛏", 176, 56);
  bindHold(breakButton, "attack");
  const placeButton = actionButton("⬛", 110, 108);
  bindHold(placeButton, "interact");

  document.body.appendChild(root);

  return {
    dispose() {
      root.remove();
    },
  };
}
